<template>
<sidebar-widget
    :start-expanded="true"
    :sections="sections"
    :pending-changes="pendingChanges"
    :headers="['Plan', 'Contribution']"
>
    <template v-slot:title>Deferred Compensation</template>

    <template v-slot:headerbutton>
        <button class="btn btn-outline-primary" @click.prevent.stop="addModalOpen = true"><i class="far fa-plus"></i> Add</button>
    </template>

     <template v-slot:row-details="props">
        <deferred-comp-details
            :is-mutation="false"
            :deferred-comp="props.row.deferred_comp"
            :deferred-comp-plan-lookup="deferredCompPlanLookup"
        />

        <button-row>
            <button class="btn btn-outline-primary" @click.prevent="removeDeferredComp(props.deferred_comp.deferred_comp.id)"><i class="far fa-trash"></i> Delete</button>

            <button class="btn btn-outline-primary" @click.prevent="editDeferredComp(props.row.deferred_comp.id)"><i class="far fa-pen"></i> Edit</button>
        </button-row>
    </template>

    <template v-slot:subrow="props">
        <section v-if="Object.keys(pendingChanges[props.row.deferred_comp.id] || []).length" class="ml-n2 mr-n2">
            <table class="table css-table widget-table table-fixed" >

                <tbody v-for="pc in pendingChanges[props.row.deferred_comp.id]" class="pending-change">
                    <tr>
                        <td class="cw-toggle position-relative pl-1">
                            <a href class="widget-toggle-wrapper collapse-trigger" role="button" :class="{'is-expanded': expandedItems[pc.id]}" @click.prevent="expandedItems[pc.id] = !expandedItems[pc.id]">
                                <collapse-toggle-button />
                            </a>
                        </td>

                        <td class="truncate-wrapper pl-0">
                            <div class="text-italic mb-0">Pending Change effective {{ $filters.date(pc.effective_on) }}</div>

                            <div>{{ pc.summary }}</div>
                        </td>
                    </tr>

                    <tr v-show="expandedItems[pc.id]">
                        <td colspan="2" class="pl-1 border-0">
                            <div class="card card-body item-details mb-2">
                                <section class="item-details-section">
                                    <deferred-comp-details
                                        :is-mutation="true"
                                        :deferred-comp="pc.deferred_comp"
                                        :diff="pc.diff"
                                        :deferred-comp-plan-lookup="deferredCompPlanLookup"
                                    />

                                    <button-row>
                                        <button class="btn btn-outline-primary" @click.prevent="removeMutation(pc.deferred_comp.id)"><i class="far fa-trash"></i> Cancel</button>
                                    </button-row>

                                </section>
                            </div>
                        </td>
                    </tr>

                </tbody>

            </table>
        </section>
    </template>

    <template v-slot:extra>
        <DeferredCompModal
            v-if="addModalOpen || editModalOpen"
            :deferred-comp="currentDeferredComp"
            :latest-mutation="currentDeferredCompMutation"
            :client="client"
            :employee="employee"
            :future-mutations="deferredCompMutations"
            :client-deferred-comp-plans="clientDeferredCompPlans"
            @close="currentMutationId = currentDeferredCompId = null; addModalOpen = editModalOpen = false"
            @updated="$emit('updated'); getData()"
        />
    </template>
</sidebar-widget>

</template>

<script>
import SidebarWidget from '@/components/SidebarWidget'
import ButtonRow from '@/components/ButtonRow'
import DeferredCompModal from './DeferredCompModal'
import DeferredCompDetails from './DeferredCompDetails'
import moment from 'moment'

export default {
    props: ['employee', 'client'],
    components: {SidebarWidget, ButtonRow, DeferredCompModal, DeferredCompDetails},
    computed: {
        deferredCompPlanLookup() {
            const result = {}

            this.clientDeferredCompPlans.forEach(p => {
                result[p.id] = p
            })

            return result
        },
        sections() {
            const currentRows = []

            this.employee.deferred_comps.filter(d => {
                return (d.is_active)
            }).forEach(d => {
                const deferredCompPlan = this.deferredCompPlanLookup[d.deferred_comp_plan_id]
                if (deferredCompPlan) {
                    currentRows.push({
                        deferred_comp: d,
                        cells: [
                            deferredCompPlan.display_name,
                            d.contribution_method == 'amount' ? '$' + d.employee_contribution_amount : d.employee_contribution_pct + '%',
                        ],
                        id: d.id,
                    })
                }
            })
            return [
                {title: 'Current', rows: currentRows, startExpanded: true},
            ]
        },
        pendingChanges() {
            const pendingChanges = {}

            const mutations = this.deferredCompMutations.filter(m => !m.is_applied && m.is_active)
            mutations.sort((a, b) => a.effective_on.localeCompare(b.effective_on))

            const mutationsByDeferredComp = {}

            mutations.forEach((m, idx) => {
                if (!mutationsByDeferredComp[m.deferred_comp_id]) {
                    mutationsByDeferredComp[m.deferred_comp_id] = []
                }

                mutationsByDeferredComp[m.deferred_comp_id].push(m)
            })

            Object.keys(mutationsByDeferredComp).forEach(deferredCompId => {
                const deferredCompMutations = mutationsByDeferredComp[deferredCompId]

                deferredCompMutations.forEach((m, idx) => {
                    const prev = (idx == 0) ? this.employee.deferred_comps.find(dc => dc.id == m.deferred_comp_id) : mutations[idx - 1]
                    const diff = this.computeDiff(m, prev)

                    if (!pendingChanges[m.deferred_comp_id]) {
                        pendingChanges[m.deferred_comp_id] = []
                    }

                    pendingChanges[m.deferred_comp_id].push({
                        deferred_comp: m,
                        prev: prev,
                        diff: diff,
                        effective_on: m.effective_on,
                        summary: this.computeDiffLine(diff),
                        id: m.id,
                    })
                })
            })

            return pendingChanges
        },
        currentDeferredComp() {
            if (this.currentDeferredCompId) {
                return this.employee.deferred_comps.find(d => d.id == this.currentDeferredCompId)
            }
            return undefined
        },
        currentDeferredCompMutation() {
            if (this.currentMutationId) {
                return this.deferredCompMutations.find(m => m.id == this.currentMutationId)
            }
            return undefined
        }
    },
    data() {
        return {
            addModalOpen: false,
            editModalOpen: false,
            removeModalOpen: false,
            clientDeferredCompPlans: [],
            currentDeferredCompId: null,
            currentMutationId: null,
            deferredCompMutations: [],

            expandedItems: {},
        }
    },
    mounted() {
        this.getData(true)
    },
    methods: {
        removeMutation(mutationId) {
            const mutation = this.deferredCompMutations.find(m => m.id == mutationId)
            if (!mutation) {return}

            if (confirm('Are you sure?')) {
                this.$store.dispatch('START_LOADING')
                return this.$api.del(`/clients/${this.client.id}/employees/${this.employee.id}/deferred-comp-mutations/${mutation.id}`).then(resp => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showInfo('Successfully deleted deferred compensation.')
                    this.$emit('updated')
                    this.getData()
                }).catch(errors => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showError(errors.__all__)
                })
            }
        },
        removeDeferredComp(deferredCompId) {
            if (confirm('Are you sure?')) {
                this.$store.dispatch('START_LOADING')
                const data = {
                    deferred_comp_id: deferredCompId,
                    effective_on: moment().format('YYYY-MM-DD'),
                    active_status: 'inactive',
                }

                return this.$api.post(`/clients/${this.client.id}/employees/${this.employee.id}/deferred-comp-mutations`, data).then(resp => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showInfo('Successfully deleted deferred compensation.')
                    this.$emit('updated')
                    this.getData()
                }).catch(errors => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showError(errors.__all__)
                })
            }
        },
        editDeferredComp(deferredCompId) {
            const deferredComp = this.employee.deferred_comps.find(dc => dc.id == deferredCompId)
            if (!deferredComp) {return}

            const mutations = this.deferredCompMutations.filter(dc => dc.is_active && (dc.deferred_comp_id == deferredComp.id))
            let latestMutation = null

            const sortKey = (m) => {
                return `${m.effective_on}-${m.created_on}-${m.id}`
            }

            for (const m of mutations) {
                if (!latestMutation) {
                    latestMutation = m
                    continue
                }

                if (sortKey(m) >= sortKey(latestMutation)) {
                    latestMutation = m
                }
            }

            if (latestMutation) {
                this.currentMutationId = latestMutation.id
            }
            this.currentDeferredCompId = deferredComp.id

            this.editModalOpen = true
        },
        getData(silent) {
            if (!silent) { this.$store.dispatch('START_LOADING') }
            this.$api.get(`/clients/${this.client.id}/deferred-comp-plans`).then(resp => {
                this.clientDeferredCompPlans = resp
                this.$api.get(`/clients/${this.client.id}/employees/${this.employee.id}/deferred-comp-mutations`).then(resp => {
                    this.$store.dispatch('STOP_LOADING')
                    this.deferredCompMutations = resp
                }).catch(errors => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showError(errors.__all__)
                })
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        computeDiff(cur, old) {
            const fields = [
                'employee_contribution_pct',
            ]
            // NB: handled separately from the list above, which coerces a falsy value to
            // 'N/A'. That collapses false and "not set" into one value, so turning a flag
            // off would register as no change at all.
            const booleanFields = [
                'is_hpi',
            ]

            const diff = {}
            for (const field of fields) {
                const curVal = cur[field] ? cur[field] : 'N/A'
                const oldVal = old[field] ? old[field] : 'N/A'

                if (curVal != oldVal) {
                    diff[field] = {old: oldVal, cur: curVal}
                }
            }

            for (const field of booleanFields) {
                const curVal = !!cur[field]
                const oldVal = !!old[field]

                if (curVal !== oldVal) {
                    diff[field] = {old: oldVal, cur: curVal}
                }
            }

            return diff
        },
        computeDiffLine(diff) {
            const fieldLabels = {
                'employee_contribution_pct': 'Employee Contribution',
                'is_hpi': 'HPI Designation',
            }

            if (Object.keys(diff).length == 0) {
                return 'No changes to be applied.' // This shouldn't happen
            }

            if (Object.keys(diff).length == 1) {
                const old = Object.values(diff)[0].old
                const cur = Object.values(diff)[0].cur

                if (diff.employee_contribution_pct) {
                    return `Employee Contribution updated from ${old}% to ${cur}%`
                }

                if (diff.is_hpi) {
                    return cur ? 'Designated a Highly Paid Individual' : 'No longer designated a Highly Paid Individual'
                }
            }

            const labels = []
            for (const field of Object.keys(diff)) {
                const label = fieldLabels[field]
                labels.push(label)
            }

            return `${labels.join(', ')} have been updated`
        },
    },
}
</script>

<style scoped lang="scss">
.pending-change {
    background-color: #e8f4f9;
    color: #0c5460;
    border-radius: 0.25rem;
    border-left: 8px solid #1c69a3;
}
</style>
